import { useState } from "react";
import { getPlanityUrl, getOpeningHours } from "@/config/standort";
import { Instagram, MapPin, Clock, Phone, Mail, Code, Layers, Monitor } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/22-studios-beauty-salon-logo-nuernberg.png";
import aivinityLogo from "@/assets/aivinity-logo.png";

const SERVICE_LINKS = [
  { to: "/nuernberg/haare", label: "Haare" },
  { to: "/nuernberg/facials", label: "Gesichtsbehandlung" },
  { to: "/nuernberg/haarentfernung", label: "Haarentfernung" },
  { to: "/nuernberg/naegel", label: "Nägel" },
];

const getLegalLinks = (basePath: string) => [
  { to: `${basePath}/impressum`, label: "Impressum" },
  { to: `${basePath}/datenschutz`, label: "Datenschutz" },
];

const AivinityBadge = () => {
  const [showPopup, setShowPopup] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowPopup(true)}
      onMouseLeave={() => setShowPopup(false)}
      onClick={() => setShowPopup(!showPopup)}
    >
      <a
        href="https://web.aivinity.de"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/10 backdrop-blur-md border border-background/20 text-background/50 text-xs font-light hover:bg-background/15 hover:border-background/40 hover:text-background/80 transition-all duration-300"
      >
        <img src={aivinityLogo} alt="aivinity" className="h-4 w-auto" />
        <span>Website by aivinity</span>
      </a>

      {showPopup && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-72 p-5 rounded-2xl bg-foreground/95 backdrop-blur-xl border border-background/20 shadow-2xl animate-scale-in origin-bottom z-50">
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-foreground/95 border-r border-b border-background/20" />
          <div className="relative flex flex-col items-center gap-3">
            <img src={aivinityLogo} alt="aivinity" className="h-8 w-auto" />
            <p className="text-background/90 text-sm font-medium tracking-wide">Webdesign & Entwicklung</p>
            <div className="w-full space-y-1.5 mt-1">
              <div className="flex items-center gap-2">
                <Code className="w-3.5 h-3.5 text-primary shrink-0" />
                <div className="h-1.5 rounded-full bg-primary/30 overflow-hidden flex-1">
                  <div className="h-full bg-primary rounded-full animate-[buildBar1_2s_ease-in-out_infinite]" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-primary/80 shrink-0" />
                <div className="h-1.5 rounded-full bg-primary/30 overflow-hidden flex-1">
                  <div className="h-full bg-primary/80 rounded-full animate-[buildBar2_2s_ease-in-out_infinite_0.3s]" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Monitor className="w-3.5 h-3.5 text-primary/60 shrink-0" />
                <div className="h-1.5 rounded-full bg-primary/30 overflow-hidden flex-1">
                  <div className="h-full bg-primary/60 rounded-full animate-[buildBar3_2s_ease-in-out_infinite_0.6s]" />
                </div>
              </div>
            </div>
            <span className="text-background/40 text-[10px] mt-1">web.aivinity.de</span>
          </div>
        </div>
      )}
    </div>
  );
};

const Footer = () => {
  const location = useLocation();
  const planityUrl = getPlanityUrl();
  const basePath = "/nuernberg";
  const serviceLinks = SERVICE_LINKS;
  const openingHours = getOpeningHours();
  const legalLinks = getLegalLinks(basePath);
  const city = "Nürnberg";
  const address = "Deidesheimer Str. 4, 90469 Nürnberg";
  const phone = "0151 7253 0302";
  const phoneTel = "+4915172530302";
  const mapsQuery = "22+Studios,+Deidesheimer+Str.+4,+90469+Nürnberg";

  return (
    <footer className="py-16 md:py-20 bg-charcoal text-background/80">
      <div className="container px-6">
        <div className="max-w-6xl mx-auto">
          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12 border-b border-background/10">
            {/* Brand & Slogan */}
            <div className="space-y-5 lg:col-span-1">
              <Link to={basePath}>
                <img
                  src={logo}
                  alt={`22 Studios ${city}`}
                  className="h-12 w-auto brightness-0 invert opacity-90"
                />
              </Link>
              <p className="text-sm text-background/50 leading-relaxed italic">
                „Dein Ort für Beauty, Stil & Wohlbefinden – in {city}."
              </p>
              <div className="flex items-center gap-3 pt-2">
                <a
                  href="https://www.instagram.com/22_studios_nbg/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-background/20 flex items-center justify-center hover:border-background/50 hover:bg-background/10 transition-all"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-background/70">
                Behandlungen
              </h4>
              <ul className="space-y-2.5">
                {serviceLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-background/50 hover:text-background transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href={planityUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                  >
                    Termin buchen →
                  </a>
                </li>
              </ul>
            </div>

            {/* Öffnungszeiten */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-background/70">
                Öffnungszeiten
              </h4>
              <ul className="space-y-2.5 text-sm text-background/50">
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 shrink-0 text-background/40" />
                    <div>
                    {openingHours.map((entry) => (
                      <p key={entry.label}>{entry.label}: {entry.time}</p>
                    ))}
                  </div>
                </li>
              </ul>
            </div>

            {/* Kontakt & Location */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold uppercase tracking-wider text-background/70">
                Kontakt & Standort
              </h4>
              <ul className="space-y-3 text-sm text-background/50">
                <li>
                  <a
                    href={`https://maps.google.com/?q=${mapsQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 hover:text-background transition-colors"
                  >
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-background/40" />
                    <span>{address}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${phoneTel}`}
                    className="flex items-center gap-2 hover:text-background transition-colors"
                  >
                    <Phone className="w-4 h-4 shrink-0 text-background/40" />
                    <span>{phone}</span>
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@22studios.de"
                    className="flex items-center gap-2 hover:text-background transition-colors"
                  >
                    <Mail className="w-4 h-4 shrink-0 text-background/40" />
                    <span>info@22studios.de</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-8 flex flex-col items-center gap-6 text-sm text-background/50">
            {/* aivinity Badge */}
            <AivinityBadge />

            <div className="w-full flex flex-col md:flex-row items-center justify-between gap-4">
              <p>© {new Date().getFullYear()} 22 Studios. Alle Rechte vorbehalten.</p>
              <div className="flex gap-6">
                {legalLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="hover:text-background transition-colors link-underline"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
